import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { ConsultationModal } from "@/components/ConsultationModal";
import { Reveal, itemAnim, heroAnim } from "@/components/Reveal";

const IMG = {
  airport: "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?auto=format&fit=crop&w=1200&q=80",
  spa: "https://images.unsplash.com/photo-1660324350911-124de1e4154a?auto=format&fit=crop&w=1200&q=80",
  celebration: "https://images.unsplash.com/photo-1668435527932-213fa9ddb871?auto=format&fit=crop&w=1200&q=80",
  chef: "https://images.unsplash.com/photo-1577219492769-b63a779fac28?auto=format&fit=crop&w=1200&q=80",
  temple: "https://images.unsplash.com/photo-1711609110590-5ad5c4599e56?auto=format&fit=crop&w=1200&q=80",
  dinner: "https://images.unsplash.com/photo-1781090876896-2dd07110eb55?auto=format&fit=crop&w=1400&q=85",
};

const copy = {
  en: {
    nav: ["Our approach", "Services", "Estates", "About"],
    contact: "Talk to our team",
    eyebrow: "GUEST EXPERIENCE & CONCIERGE",
    heroTitle: <>Every stay, <em>thoughtfully</em> arranged.</>,
    heroText: "From the moment guests land in Bali to their last sunset by the pool, our concierge team takes care of the details, so every stay feels effortless and every review glows.",
    seeServices: "See our services",
    servicesEyebrow: "OUR CONCIERGE SERVICES",
    services: [
      ["Airport transfer", "A private driver waiting at arrivals with a name sign, cold water and a cool towel. Return transfers timed to every flight.", IMG.airport],
      ["In-villa spa", "Balinese massage and spa treatments by trained therapists, in the comfort and privacy of the villa.", IMG.spa],
      ["Celebration decoration", "Birthdays, anniversaries, proposals and honeymoons, styled with flowers, petals and candles before guests walk in.", IMG.celebration],
      ["Private chef", "Breakfast by the pool or a candlelit dinner at home. Menus from Balinese classics to international dishes, prepared in the villa kitchen.", IMG.chef],
      ["Tours & recreation", "Temples, rice terraces, waterfalls, sunrise treks, surf lessons and island day trips, planned with trusted local guides and private drivers.", IMG.temple],
    ],
    howEyebrow: "HOW IT WORKS",
    how: [
      ["Request", "Guests message our concierge on WhatsApp before or during their stay."],
      ["We arrange", "We confirm the details, timing and price, and book trusted local partners."],
      ["Enjoy", "Everything arrives on time. Guests simply enjoy their stay."],
    ],
    whyEyebrow: "WHY IT MATTERS FOR OWNERS",
    whyTitle: <>Happier guests. <em>Better</em> reviews.</>,
    whyPoints: [
      "Memorable stays lead to five-star reviews and stronger ranking on every platform.",
      "Repeat guests and recommendations bring more direct bookings.",
      "Extra services add value to every stay, at no effort to you.",
    ],
    ctaTitle: <>Planning a stay or <br /><em>a special moment?</em></>,
    ctaText: "Tell us what you have in mind. Our concierge team will take care of the rest.",
    footer: "Keeping Bali’s finest estates in their best light.",
    footerDist: "Distribution Channels",
  },
  id: {
    nav: ["Pendekatan kami", "Layanan", "Properti", "Tentang"],
    contact: "Hubungi tim kami",
    eyebrow: "PENGALAMAN TAMU & CONCIERGE",
    heroTitle: <>Setiap masa inap, <em>disiapkan</em> dengan perhatian.</>,
    heroText: "Dari momen tamu mendarat di Bali hingga senja terakhir di tepi kolam, tim concierge kami mengurus setiap detail — sehingga setiap masa inap terasa tanpa beban dan setiap ulasan bersinar.",
    seeServices: "Lihat layanan kami",
    servicesEyebrow: "LAYANAN CONCIERGE KAMI",
    services: [
      ["Antar-jemput bandara", "Sopir privat menanti di area kedatangan dengan papan nama, air dingin, dan handuk dingin. Penjemputan pulang disesuaikan dengan setiap jadwal penerbangan.", IMG.airport],
      ["Spa di dalam villa", "Pijat Balinese dan perawatan spa oleh terapis terlatih, dalam kenyamanan dan privasi villa.", IMG.spa],
      ["Dekorasi perayaan", "Ulang tahun, anniversary, lamaran, dan bulan madu — ditata dengan bunga, kelopak, dan lilin sebelum tamu tiba.", IMG.celebration],
      ["Koki privat", "Sarapan di tepi kolam atau makan malam romantis di villa. Menu dari hidangan klasik Bali hingga internasional, disiapkan di dapur villa.", IMG.chef],
      ["Tur & rekreasi", "Pura, sawah terasering, air terjun, pendakian sunrise, les selancar, dan tur pulau — direncanakan bersama pemandu lokal tepercaya dan sopir privat.", IMG.temple],
    ],
    howEyebrow: "CARA KERJANYA",
    how: [
      ["Minta", "Tamu mengirim pesan ke concierge kami via WhatsApp sebelum atau selama masa inap."],
      ["Kami atur", "Kami konfirmasi detail, waktu, dan harga, lalu memesan mitra lokal tepercaya."],
      ["Nikmati", "Semuanya tiba tepat waktu. Tamu tinggal menikmati masa inapnya."],
    ],
    whyEyebrow: "MENGAPA PENTING BAGI PEMILIK",
    whyTitle: <>Tamu lebih bahagia. Ulasan lebih <em>baik.</em></>,
    whyPoints: [
      "Masa inap yang berkesan menghasilkan ulasan bintang lima dan peringkat yang lebih kuat di setiap platform.",
      "Tamu yang kembali dan rekomendasi mendatangkan lebih banyak pemesanan langsung.",
      "Layanan tambahan menambah nilai setiap masa inap, tanpa merepotkan Anda.",
    ],
    ctaTitle: <>Merencanakan masa inap <br /><em>atau momen spesial?</em></>,
    ctaText: "Ceritakan apa yang Anda bayangkan. Tim concierge kami akan mengurus sisanya.",
    footer: "Menjaga villa terbaik Bali dalam cahaya terbaiknya.",
    footerDist: "Kanal Distribusi",
  },
};

export default function Concierge({ lang, setLang }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [active, setActive] = useState(0);
  const t = copy[lang];

  useEffect(() => {
    document.title = "Guest Experience & Concierge · Eden Hospitality Bali";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "Airport transfers, in-villa spa, celebration decoration, private chef and tours across Bali, arranged by the Eden Hospitality concierge team.");
  }, []);

  return <div className="site-shell">
    <Nav lang={lang} setLang={setLang} onConsult={() => setModalOpen(true)} labels={t.nav} ctaLabel={t.contact} />

    <main id="top">
      <section className="conc-hero" data-testid="concierge-hero">
        <motion.p className="eyebrow gold" {...heroAnim(0.2)}>{t.eyebrow}</motion.p>
        <motion.h1 data-testid="concierge-heading" {...heroAnim(0.35)}>{t.heroTitle}</motion.h1>
        <motion.p className="sub" {...heroAnim(0.55)}>{t.heroText}</motion.p>
        <motion.div className="conc-hero-actions" {...heroAnim(0.75)}>
          <button className="button button-terra" onClick={() => setModalOpen(true)} data-testid="concierge-hero-cta">{t.contact} <ArrowUpRight size={16} /></button>
          <button className="button button-outline-light" onClick={() => document.getElementById("concierge-services")?.scrollIntoView({ behavior: "smooth" })} data-testid="concierge-services-button">{t.seeServices} <ArrowUpRight size={16} /></button>
        </motion.div>
      </section>

      <section id="concierge-services" data-testid="concierge-services">
        <div className="conc-split">
          <Reveal>
            <p className="eyebrow">{t.servicesEyebrow}</p>
            <div>
              {t.services.map(([title, text, img], i) => (
                <div className="conc-item" key={title} onMouseEnter={() => setActive(i)} data-testid={`conc-item-${i}`}>
                  <span>0{i + 1}</span>
                  <div className="conc-item-body"><strong>{title}</strong><p>{text}</p></div>
                  <ArrowUpRight size={17} />
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <figure className="conc-photo" data-testid="concierge-photo">
              {t.services.map(([title, , img], i) => (
                <div key={title} className={`ph ${active === i ? "active" : ""}`} style={{ backgroundImage: `url(${img})` }} data-testid={`conc-photo-${i}`} />
              ))}
              <figcaption data-testid="concierge-photo-caption">{t.services[active][0]}</figcaption>
            </figure>
          </Reveal>
        </div>
      </section>

      <section className="conc-how" data-testid="concierge-how">
        <Reveal><p className="eyebrow">{t.howEyebrow}</p></Reveal>
        <div className="conc-how-grid">
          {t.how.map(([title, text], i) => (
            <motion.div className="conc-how-col" key={title} data-testid={`conc-how-${i}`} {...itemAnim(i)}>
              <h3>{title}</h3><p>{text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="why" data-testid="concierge-why">
        <Reveal className="why-text">
          <p className="eyebrow">{t.whyEyebrow}</p>
          <h2>{t.whyTitle}</h2>
          <ul className="why-list">
            {t.whyPoints.map((point, i) => <li key={i} data-testid={`conc-why-point-${i}`}><Check size={15} />{point}</li>)}
          </ul>
        </Reveal>
        <Reveal className="why-photo" delay={0.15}><div className="arch-photo" style={{ backgroundImage: `url(${IMG.dinner})` }} data-testid="concierge-arch-photo" /></Reveal>
      </section>

      <section className="closing section-dark" data-testid="concierge-cta">
        <Reveal className="closing-content">
          <p className="eyebrow gold">EDEN HOSPITALITY</p>
          <h2>{t.ctaTitle}</h2>
          <p>{t.ctaText}</p>
          <button className="button button-light" onClick={() => setModalOpen(true)} data-testid="concierge-closing-cta">{t.contact} <ArrowUpRight size={17} /></button>
        </Reveal>
        <div className="closing-orb" />
      </section>
    </main>

    <Footer footerText={t.footer} contactLabel={t.contact} distLabel={t.footerDist} onConsult={() => setModalOpen(true)} />
    <ConsultationModal lang={lang} open={modalOpen} onClose={() => setModalOpen(false)} ctaLabel={t.contact} />
  </div>;
}
